import { useMemo } from 'react';
import { Button, Col, Row, Stack } from 'react-bootstrap';

import { BsDownload } from 'react-icons/bs';
import { StatusPill } from '../../../../v2/js/components/StatusPill';
import { WorkflowScopes } from '../../../../v2/js/constants/workflows';
import { ProviderReference } from '../../../api';
import { Card } from '../../../app/components/Card';
import { useProvider } from '../../../app/hooks/providers';
import { useDocumentTitle } from '../../../app/hooks/use-document-title';

const Reference = ({
  reference,
  isLoading,
}: {
  reference?: ProviderReference;
  isLoading?: boolean;
}) => {
  return (
    <div>
      <div className="d-flex justify-content-end mb-2">
        {reference?.current_status && (
          <StatusPill scope={WorkflowScopes.ProviderReference} status={reference.current_status} />
        )}
      </div>
      <Row>
        <Col xs={12} lg={6}>
          <Card.Info isLoading={isLoading} label="Full name">
            {reference?.full_name ? reference.full_name : 'N/A'}
          </Card.Info>
        </Col>
        <Col xs={12} lg={6}>
          <Card.Info isLoading={isLoading} label="Email">
            {reference?.email ? reference.email : 'N/A'}
          </Card.Info>
        </Col>
        <Col xs={12} lg={12}>
          <Card.Info isLoading={isLoading} label="Reference Letter">
            {reference?.recommendation_letter ? (
              <Button
                size="sm"
                variant="outline-primary"
                href={reference?.recommendation_letter.file}
                target="_blank"
                rel="noreferrer"
              >
                <BsDownload className="tw:inline" /> Download
              </Button>
            ) : (
              'N/A'
            )}
          </Card.Info>
        </Col>
      </Row>
    </div>
  );
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export const ProfessionalTraits = () => {
  useDocumentTitle('Provider Professional Traits');
  const { isProviderLoading, provider } = useProvider();

  const activeReferences = useMemo(
    () => provider?.references?.filter((reference) => !reference.terminated_at) || [],
    [provider?.references]
  );

  return (
    <>
      <Row className="justify-content-center">
        <Col xs={12} md={10} lg={8} xl={6}>
          <Card.SubCard className="p-4 mb-4">
            <h5>Professional Links</h5>
            <Row>
              <Col xs={12} lg={6}>
                <Card.Info label="LinkedIn Link" isLoading={isProviderLoading}>
                  {provider?.linkedin_link ? (
                    <a href={provider.linkedin_link} target="_blank" rel="noreferrer">
                      {provider?.linkedin_link}
                    </a>
                  ) : (
                    'N/A'
                  )}
                </Card.Info>
              </Col>
              <Col xs={12} lg={6}>
                <Card.Info label="CV" isLoading={isProviderLoading}>
                  {provider?.cv ? (
                    <Button
                      size="sm"
                      variant="outline-primary"
                      href={provider.cv}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <BsDownload className="tw:inline" /> Download CV
                    </Button>
                  ) : (
                    'N/A'
                  )}
                </Card.Info>
              </Col>
            </Row>
          </Card.SubCard>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col xs={12} md={10} lg={8} xl={6}>
          <Card.SubCard className="p-4 mb-4">
            <h5>References</h5>
            <Stack direction="vertical">
              {isProviderLoading && (
                <>
                  <Reference isLoading={isProviderLoading} />
                  <hr />
                  <Reference isLoading={isProviderLoading} />
                </>
              )}
              {activeReferences?.map((reference, i) => (
                <div key={reference?.id}>
                  <Reference reference={reference} isLoading={isProviderLoading} />
                  {i !== activeReferences.length - 1 && <hr />}
                </div>
              ))}
            </Stack>
          </Card.SubCard>
        </Col>
      </Row>
    </>
  );
};
